import React, { useContext, useState, useRef, useEffect } from 'react';
import { UserContext } from './UserContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaUser, FaClipboardList, FaDatabase,
  FaKey, FaUsers, FaUserShield, FaBuilding, FaBoxOpen, FaHistory,
  FaChevronLeft, FaChevronRight, FaBars, FaSun, FaMoon, FaBalanceScale,
  FaUpload, FaFileImport, FaGlobe, FaHome,
  FaExchangeAlt, FaExclamationTriangle, FaFileAlt, FaSyncAlt, FaChartBar, FaCog, FaShieldAlt, FaCommentDots,
  FaFileInvoiceDollar, FaEnvelope
} from 'react-icons/fa';
import './Navbar.css';
import { SHOW_MTC_MENUS } from './featureFlags';
import { hasMenu, isOrgUser } from './PrivateRoute';

/* ─────────── Theme helper ─────────── */
const THEME_KEY = 'pip-theme';

// Two themes only. Until the user picks one, the OS preference decides
// (and is followed live); once they toggle, their choice is saved.
const darkQuery = window.matchMedia('(prefers-color-scheme: dark)');

function getSavedTheme() {
  try {
    const t = localStorage.getItem(THEME_KEY);
    return t === 'light' || t === 'dark' ? t : null;
  } catch (_) {
    return null;
  }
}

function systemTheme() {
  return darkQuery.matches ? 'dark' : 'light';
}

function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
}

const themeIcon = { light: <FaSun />, dark: <FaMoon /> };
const themeLabel = { light: 'Light', dark: 'Dark' };

function Navbar() {
  const { user }                    = useContext(UserContext);
  const [collapsed, setCollapsed]   = useState(false);
  const navigate                    = useNavigate();
  const location                    = useLocation();
  // Auto-expand the dropdown that contains the current route so the user
  // doesn't have to click the parent to see the active submenu item.
  const [adminOpen, setAdminOpen]   = useState(() => location.pathname.toLowerCase().startsWith('/administrator'));
  const [captureOpen, setCaptureOpen] = useState(() => location.pathname.toLowerCase().startsWith('/datacapturer'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme]           = useState(() => getSavedTheme() || systemTheme());
  const [flyoutTop, setFlyoutTop]   = useState(0);

  // Keep submenu state in sync if the user navigates between sections.
  useEffect(() => {
    const p = location.pathname.toLowerCase();
    if (p.startsWith('/administrator')) setAdminOpen(true);
    if (p.startsWith('/datacapturer'))  setCaptureOpen(true);
  }, [location.pathname]);
  const adminMenuRef                = useRef();
  const flyoutRef                   = useRef();
  const captureMenuRef              = useRef();
  const captureFlyoutRef            = useRef();

  /* ─────────── apply theme on mount + change ─────────── */
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Follow OS theme changes while the user hasn't picked one explicitly.
  useEffect(() => {
    const onChange = () => { if (!getSavedTheme()) setTheme(systemTheme()); };
    darkQuery.addEventListener('change', onChange);
    return () => darkQuery.removeEventListener('change', onChange);
  }, []);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    try { localStorage.setItem(THEME_KEY, next); } catch (_) {}
    setTheme(next);
  };

  // Logout + refreshPermissions moved to components/UserMenu.js
  // (rendered in AppHeader top-right next to the notification bell).

  /* ─────────── routes ─────────── */
  // Data Capturer is no longer a single page — it's a submenu with one route
  // per capture form. The top-level entry below is a button that toggles the
  // submenu instead of navigating.
  // Each entry can declare a `require` permission; rendered list is filtered
  // by `can()` below (defined after user checks).
  // `mtc: true` flags entries gated by REACT_APP_SHOW_MTC_MENUS (build-time).
  // When the flag is unset/false those entries are hidden in the sidebar AND
  // their routes return a "feature disabled" page (see App.js + MtcGate).
  // `menu` is the key an organisation must be assigned (Manage Organisations).
  const mainRoutes = [
    { path: '/dashboard',       menu: 'dashboard',          label: 'Dashboard',    icon: <FaChartBar />,     require: null },
    { path: '/Pips/pips',       menu: 'pips',               label: 'PIPs',         icon: <FaUser />,         require: 'pips.read' },
    { path: '/un-sanctions',    menu: 'un_sanctions',       label: 'UN Sanctions', icon: <FaBalanceScale />, require: 'un_sanctions.search' },
    { path: '/uk-sanctions',    menu: 'uk_sanctions',       label: 'UK Sanctions', icon: <FaBalanceScale />, require: 'uk_sanctions.search' },
    { path: '/customers',       menu: 'kyc',                label: 'KYC',          icon: <FaUser />,         require: 'customers.read' },
    { path: '/cases',           menu: 'kyc',                label: 'Cases',        icon: <FaUserShield />,   require: 'cases.read' },
    { path: '/transactions',    menu: 'transactions',       label: 'Transactions', icon: <FaExchangeAlt />,  require: 'transactions.read',     mtc: true },
    { path: '/tm-alerts',       menu: 'tm_alerts',          label: 'TM Alerts',    icon: <FaExclamationTriangle />, require: 'tm_alerts.read', mtc: true },
    { path: '/str-reports',     menu: 'str_reports',        label: 'STR Reports',  icon: <FaFileAlt />,      require: 'str_reports.read',      mtc: true },
    { path: '/customer-reviews', menu: 'cdd_reviews',       label: 'CDD Reviews',  icon: <FaSyncAlt />,      require: 'customer_reviews.read', mtc: true },
    { path: '/watchlist',       menu: 'watchlist',          label: 'Watchlist',    icon: <FaShieldAlt />,    require: 'org_watchlist.read',    mtc: true },
    { path: '/tm-rules',        menu: 'tm_rules',           label: 'TM Rules',     icon: <FaCog />,          require: 'tm_rules.read',         mtc: true },
    { path: '/limit-rules',     menu: 'limits',             label: 'Limits',       icon: <FaBalanceScale />, require: 'limits.read',           mtc: true }
  ];

  // Rendered as the last main menu entry (below all submenus). Permission-gated below.
  const auditRoute = { path: '/audit', label: 'Audit Trail', icon: <FaHistory /> };

  /* ─────────── outside-click for profile popup + admin flyout ─────────── */
  useEffect(() => {
    const clickOut = e => {
      if (
        flyoutRef.current && !flyoutRef.current.contains(e.target) &&
        adminMenuRef.current && !adminMenuRef.current.contains(e.target)
      ) {
        setAdminOpen(false);
      }
      if (
        captureFlyoutRef.current && !captureFlyoutRef.current.contains(e.target) &&
        captureMenuRef.current && !captureMenuRef.current.contains(e.target)
      ) {
        setCaptureOpen(false);
      }
    };
    document.addEventListener('mousedown', clickOut);
    return () => document.removeEventListener('mousedown', clickOut);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && mobileMenuOpen) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileMenuOpen]);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  if (!user) return null;

  // Permission helper — system admins bypass; otherwise check user.permissions.
  // Everything below uses permissions only — no role checks.
  const can = (perm) =>
    !!user && (
      user.is_system_admin === true ||
      (Array.isArray(user.permissions) && user.permissions.includes(perm))
    );
  const canAny = (...perms) => perms.some(can);

  // Data Capturer submenu — per-form permissions
  const canCapturePip      = can('pips.create');
  const canImportPips      = can('pips.import');
  const canUploadUn        = can('un_sanctions.upload');
  const canUploadUk        = can('uk_sanctions.upload');
  const hasAnyCaptureMenu  = canCapturePip || canImportPips || canUploadUn || canUploadUk;

  // Administrator submenu — per-page permissions
  const canManageUsers         = can('users.read');
  const canManageRoles         = can('roles.read') && !isOrgUser(user);   // roles are managed by system admins only
  const canManageOrganisations = can('organisations.read');
  const canManagePackages      = can('packages.read');
  // Billing: PIP Intel staff see every organisation's invoices; an organisation's users their own (invoices.view_own).
  const canManageInvoices      = isOrgUser(user) ? can('invoices.view_own') : can('invoices.read');
  const canSendMessages        = can('org_messages.read') && !isOrgUser(user);   // messages to organisations: PIP Intel staff only
  const canManagePermissions   = can('permissions.read');
  const hasAnyAdminMenu = canManageUsers || canManageRoles || canManageOrganisations ||
                          canManagePackages || canManageInvoices || canSendMessages || canManagePermissions;

  // Audit + history entries (not part of mainRoutes — rendered separately)
  const canReadAudit   = can('audit.read');
  // Feedback inbox: PIP Intel staff = users without an organisation.
  const canReadFeedback = !user.organisation_id;
  const canReadHistory = canAny('pips.read', 'pips.bulk_search', 'audit.read') &&
                         hasMenu(user, 'pip_search_history');

  // Filter the declared main routes by the permission they require.
  // Filter by permission AND by the MTC feature flag — entries marked
  // `mtc: true` only show when REACT_APP_SHOW_MTC_MENUS=true at build time.
  const visibleMainRoutes = mainRoutes
    .filter(r => !r.require || can(r.require))
    .filter(r => !r.mtc || SHOW_MTC_MENUS)
    .filter(r => hasMenu(user, r.menu));

  /* ─────────── render ─────────── */
  return (
    <>
      {/* Mobile toggle button */}
      <button
        className="mobile-menu-toggle"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
        style={{ background: 'var(--accent)', border: 'none', padding: 0, color: '#fff' }}
      >
        <FaBars size={20} />
      </button>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="mobile-overlay active"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <div className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        {/* Header */}
        <div className="sidebar-header">
          <div
            className="toggle-btn"
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? 'Expand menu' : 'Collapse menu'}
          >
            {collapsed ? <FaChevronRight size={14} /> : <FaChevronLeft size={14} />}
          </div>

          {/* Brand label (page title now lives in the top header bar) */}
          {!collapsed && (
            <div className="page-title">PIP Intel</div>
          )}

          {/* Theme toggle */}
          <button
            className="theme-toggle-btn"
            onClick={toggleTheme}
            title={`Theme: ${themeLabel[theme]} — click to switch`}
            aria-label="Toggle colour theme"
          >
            {themeIcon[theme]}
          </button>
        </div>

        {/* Main menu */}
        <div className="menu">
          {visibleMainRoutes.map(({ path, label, icon }) => (
            <div
              key={path}
              className={`menu-item ${location.pathname === path ? 'active' : ''}`}
              onClick={() => navigate(path)}
              title={collapsed ? label : ''}
            >
              <span className="menu-icon">{icon}</span>
              <span className="menu-label">{label}</span>
            </div>
          ))}

          {/* PIP Search History — requires permission to read PIPs or audit trail */}
          {canReadHistory && (
            <div
              className={`menu-item ${location.pathname === '/pip-search-history' ? 'active' : ''}`}
              onClick={() => navigate('/pip-search-history')}
              title={collapsed ? 'PIP Search History' : ''}
            >
              <span className="menu-icon"><FaClipboardList /></span>
              <span className="menu-label">PIP Search History</span>
            </div>
          )}

          {/* Data Capturer dropdown — parent shown if user has ANY capture perm.
              Each sub-item independently gated on its own permission. */}
          {hasAnyCaptureMenu && (
            <>
              <div
                ref={captureMenuRef}
                className={`menu-item ${location.pathname.startsWith('/datacapturer') ? 'active' : ''}`}
                onClick={() => {
                  if (collapsed) {
                    const rect = captureMenuRef.current?.getBoundingClientRect();
                    setFlyoutTop(rect?.top ?? 0);
                    setCaptureOpen(prev => !prev);
                  } else {
                    setCaptureOpen(prev => !prev);
                  }
                }}
                title={collapsed ? 'Data Capturer' : ''}
              >
                <span className="menu-icon"><FaDatabase /></span>
                <span className="menu-label">Data Capturer</span>
                {!collapsed && (
                  <span className={`menu-chevron ${captureOpen ? 'open' : ''}`}>
                    <FaChevronRight size={10} />
                  </span>
                )}
              </div>

              {captureOpen && !collapsed && (
                <div className="submenu" style={{ display: 'block' }}>
                  {canCapturePip && (
                    <div
                      className={`submenu-item ${location.pathname === '/datacapturer/domestic-pip' ? 'active' : ''}`}
                      onClick={() => navigate('/datacapturer/domestic-pip')}
                    >
                      <FaHome className="submenu-icon" /> Capture Domestic PIP
                    </div>
                  )}
                  {canCapturePip && (
                    <div
                      className={`submenu-item ${location.pathname === '/datacapturer/foreign-pip' ? 'active' : ''}`}
                      onClick={() => navigate('/datacapturer/foreign-pip')}
                    >
                      <FaGlobe className="submenu-icon" /> Capture Foreign PIP
                    </div>
                  )}
                  {canImportPips && (
                    <div
                      className={`submenu-item ${location.pathname === '/datacapturer/import-pips' ? 'active' : ''}`}
                      onClick={() => navigate('/datacapturer/import-pips')}
                    >
                      <FaFileImport className="submenu-icon" /> Import PIPs
                    </div>
                  )}
                  {canUploadUn && (
                    <div
                      className={`submenu-item ${location.pathname === '/datacapturer/upload-un-sanctions' ? 'active' : ''}`}
                      onClick={() => navigate('/datacapturer/upload-un-sanctions')}
                    >
                      <FaUpload className="submenu-icon" /> Upload UN Sanctions
                    </div>
                  )}
                  {canUploadUk && (
                    <div
                      className={`submenu-item ${location.pathname === '/datacapturer/upload-uk-sanctions' ? 'active' : ''}`}
                      onClick={() => navigate('/datacapturer/upload-uk-sanctions')}
                    >
                      <FaUpload className="submenu-icon" /> Upload UK Sanctions
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Administrator dropdown — parent shown if user has ANY admin perm.
              Each sub-item independently gated on its own permission. */}
          {hasAnyAdminMenu && (
            <>
              <div
                ref={adminMenuRef}
                className={`menu-item ${location.pathname.startsWith('/administrator') ? 'active' : ''}`}
                onClick={() => {
                  if (collapsed) {
                    const rect = adminMenuRef.current?.getBoundingClientRect();
                    setFlyoutTop(rect?.top ?? 0);
                    setAdminOpen(prev => !prev);
                  } else {
                    setAdminOpen(prev => !prev);
                  }
                }}
                title={collapsed ? 'Administrator' : ''}
              >
                <span className="menu-icon"><FaKey /></span>
                <span className="menu-label">Administrator</span>
                {!collapsed && (
                  <span className={`menu-chevron ${adminOpen ? 'open' : ''}`}>
                    <FaChevronRight size={10} />
                  </span>
                )}
              </div>

              {/* Expanded submenu (sidebar open) */}
              {adminOpen && !collapsed && (
                <div className="submenu" style={{ display: 'block' }}>
                  {canManageUsers && (
                    <div
                      className={`submenu-item ${location.pathname === '/administrator/manageusers' ? 'active' : ''}`}
                      onClick={() => navigate('/administrator/manageusers')}
                    >
                      <FaUsers className="submenu-icon" /> Manage Users
                    </div>
                  )}
                  {canManageRoles && (
                    <div
                      className={`submenu-item ${location.pathname === '/administrator/manageroles' ? 'active' : ''}`}
                      onClick={() => navigate('/administrator/manageroles')}
                    >
                      <FaUserShield className="submenu-icon" /> Manage Roles
                    </div>
                  )}
                  {canManageOrganisations && (
                    <div
                      className={`submenu-item ${location.pathname === '/administrator/manageorganisations' ? 'active' : ''}`}
                      onClick={() => navigate('/administrator/manageorganisations')}
                    >
                      <FaBuilding className="submenu-icon" /> Manage Organisations
                    </div>
                  )}
                  {canManagePackages && (
                    <div
                      className={`submenu-item ${location.pathname === '/administrator/managepackages' ? 'active' : ''}`}
                      onClick={() => navigate('/administrator/managepackages')}
                    >
                      <FaBoxOpen className="submenu-icon" /> Manage Packages
                    </div>
                  )}
                  {canManageInvoices && (
                    <div
                      className={`submenu-item ${location.pathname === '/administrator/invoices' ? 'active' : ''}`}
                      onClick={() => navigate('/administrator/invoices')}
                    >
                      <FaFileInvoiceDollar className="submenu-icon" /> Invoices
                    </div>
                  )}
                  {canSendMessages && (
                    <div
                      className={`submenu-item ${location.pathname === '/administrator/messages' ? 'active' : ''}`}
                      onClick={() => navigate('/administrator/messages')}
                    >
                      <FaEnvelope className="submenu-icon" /> Messages
                    </div>
                  )}
                  {canManagePermissions && (
                    <div
                      className={`submenu-item ${location.pathname === '/administrator/manage-permissions' ? 'active' : ''}`}
                      onClick={() => navigate('/administrator/manage-permissions')}
                    >
                      <FaKey className="submenu-icon" /> Manage Permissions
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Feedback — sent with the Feedback button; PIP Intel staff only */}
          {canReadFeedback && (
            <div
              className={`menu-item ${location.pathname === '/feedback' ? 'active' : ''}`}
              onClick={() => navigate('/feedback')}
              title={collapsed ? 'Feedback' : ''}
            >
              <span className="menu-icon"><FaCommentDots /></span>
              <span className="menu-label">Feedback</span>
            </div>
          )}

          {/* Audit Trail — permission-gated, always rendered LAST in the main menu */}
          {canReadAudit && (
            <div
              className={`menu-item ${location.pathname === auditRoute.path ? 'active' : ''}`}
              onClick={() => navigate(auditRoute.path)}
              title={collapsed ? auditRoute.label : ''}
            >
              <span className="menu-icon">{auditRoute.icon}</span>
              <span className="menu-label">{auditRoute.label}</span>
            </div>
          )}
        </div>

        {/* Profile icon + Logout + Refresh perms moved to AppHeader (top-right
            UserMenu). The sidebar no longer renders any profile chunk. */}
      </div>

      {/* Data Capturer flyout (collapsed sidebar) — per-item permission-gated. */}
      {hasAnyCaptureMenu && collapsed && captureOpen && (
        <div
          ref={captureFlyoutRef}
          className="submenu-flyout"
          style={{ top: flyoutTop }}
        >
          <div className="submenu-flyout__title">
            <FaDatabase size={12} /> Data Capturer
          </div>
          {canCapturePip && (
            <div
              className={`submenu-flyout__item ${location.pathname === '/datacapturer/domestic-pip' ? 'active' : ''}`}
              onClick={() => { navigate('/datacapturer/domestic-pip'); setCaptureOpen(false); }}
            >
              <FaHome /> Capture Domestic PIP
            </div>
          )}
          {canCapturePip && (
            <div
              className={`submenu-flyout__item ${location.pathname === '/datacapturer/foreign-pip' ? 'active' : ''}`}
              onClick={() => { navigate('/datacapturer/foreign-pip'); setCaptureOpen(false); }}
            >
              <FaGlobe /> Capture Foreign PIP
            </div>
          )}
          {canImportPips && (
            <div
              className={`submenu-flyout__item ${location.pathname === '/datacapturer/import-pips' ? 'active' : ''}`}
              onClick={() => { navigate('/datacapturer/import-pips'); setCaptureOpen(false); }}
            >
              <FaFileImport /> Import PIPs
            </div>
          )}
          {canUploadUn && (
            <div
              className={`submenu-flyout__item ${location.pathname === '/datacapturer/upload-un-sanctions' ? 'active' : ''}`}
              onClick={() => { navigate('/datacapturer/upload-un-sanctions'); setCaptureOpen(false); }}
            >
              <FaUpload /> Upload UN Sanctions
            </div>
          )}
          {canUploadUk && (
            <div
              className={`submenu-flyout__item ${location.pathname === '/datacapturer/upload-uk-sanctions' ? 'active' : ''}`}
              onClick={() => { navigate('/datacapturer/upload-uk-sanctions'); setCaptureOpen(false); }}
            >
              <FaUpload /> Upload UK Sanctions
            </div>
          )}
        </div>
      )}

      {/* Admin flyout (collapsed sidebar) — per-item permission-gated. */}
      {hasAnyAdminMenu && collapsed && adminOpen && (
        <div
          ref={flyoutRef}
          className="submenu-flyout"
          style={{ top: flyoutTop }}
        >
          <div className="submenu-flyout__title">
            <FaKey size={12} /> Administrator
          </div>
          {canManageUsers && (
            <div
              className={`submenu-flyout__item ${location.pathname === '/administrator/manageusers' ? 'active' : ''}`}
              onClick={() => { navigate('/administrator/manageusers'); setAdminOpen(false); }}
            >
              <FaUsers /> Manage Users
            </div>
          )}
          {canManageRoles && (
            <div
              className={`submenu-flyout__item ${location.pathname === '/administrator/manageroles' ? 'active' : ''}`}
              onClick={() => { navigate('/administrator/manageroles'); setAdminOpen(false); }}
            >
              <FaUserShield /> Manage Roles
            </div>
          )}
          {canManageOrganisations && (
            <div
              className={`submenu-flyout__item ${location.pathname === '/administrator/manageorganisations' ? 'active' : ''}`}
              onClick={() => { navigate('/administrator/manageorganisations'); setAdminOpen(false); }}
            >
              <FaBuilding /> Manage Organisations
            </div>
          )}
          {canManagePackages && (
            <div
              className={`submenu-flyout__item ${location.pathname === '/administrator/managepackages' ? 'active' : ''}`}
              onClick={() => { navigate('/administrator/managepackages'); setAdminOpen(false); }}
            >
              <FaBoxOpen /> Manage Packages
            </div>
          )}
          {canManageInvoices && (
            <div
              className={`submenu-flyout__item ${location.pathname === '/administrator/invoices' ? 'active' : ''}`}
              onClick={() => { navigate('/administrator/invoices'); setAdminOpen(false); }}
            >
              <FaFileInvoiceDollar /> Invoices
            </div>
          )}
          {canSendMessages && (
            <div
              className={`submenu-flyout__item ${location.pathname === '/administrator/messages' ? 'active' : ''}`}
              onClick={() => { navigate('/administrator/messages'); setAdminOpen(false); }}
            >
              <FaEnvelope /> Messages
            </div>
          )}
          {canManagePermissions && (
            <div
              className={`submenu-flyout__item ${location.pathname === '/administrator/manage-permissions' ? 'active' : ''}`}
              onClick={() => { navigate('/administrator/manage-permissions'); setAdminOpen(false); }}
            >
              <FaKey /> Manage Permissions
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default Navbar;
